import { Step as ModelStep } from "@redotech/redo-model/return-flow";
import { overrideAdjustmentFromModel } from "@redotech/redo-model/return-flow-step-from-model";
import {
  isOverrideAdjustmentStepValid,
  OverrideAdjustmentState,
} from "@redotech/redo-model/return-flow-step-valid";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import MoneyIcon from "@redotech/redo-web/icon-old/share-money.svg";
import { produce } from "immer";
import * as React from "react";
import { StepDownstream, StepType, StepTypeDetailsProps } from "../step";

export const OVERRIDE_ADJUSTMENT = (
  Details: React.ComponentType<StepTypeDetailsProps<OverrideAdjustmentState>>,
): StepType<OverrideAdjustmentState, ModelStep.OverrideAdjustment> => ({
  Details: (props: StepTypeDetailsProps<OverrideAdjustmentState>) =>
    React.createElement(Details, props),
  customTitle(state) {
    return state.customTitle;
  },
  title: "Override Adjustment",
  valid(state) {
    return isOverrideAdjustmentStepValid(state).valid;
  },
  Icon: MoneyIcon,
  description(state) {
    // Show adjustmentFlat first if not 0, then show adjustmentPercent if not 0, then default to $0
    return `Set adjustment to ${
      state?.adjustmentFlat && +state.adjustmentFlat !== 0
        ? // Show negative sign in front of dollar sign
          `${+state.adjustmentFlat < 0 ? "-" : ""}$${Math.abs(
            +state.adjustmentFlat,
          )}`
        : state?.adjustmentPercent && +state.adjustmentPercent !== 0
          ? `${state.adjustmentPercent}%`
          : "$0"
    }`;
  },
  downstream(state) {
    const result: StepDownstream[] = [];
    if (state.next !== undefined) {
      result.push({ id: state.next });
    }
    return result;
  },
  maxDownstream() {
    return 1;
  },
  empty: { next: undefined, adjustmentFlat: "0", adjustmentPercent: "0" },
  stepDeleted(state, stepId) {
    return produce(state, (state) => {
      if (state.next === stepId) {
        state.next = undefined;
      }
    });
  },
  updateConnection(state, handleIndex, targetStepId) {
    if (handleIndex !== 0) {
      throw new Error(
        `Invalid handle index ${handleIndex} for Override Adjustment step`,
      );
    }
    return produce(state, (draft) => {
      draft.next = targetStepId;
    });
  },
  removeConnection(state, targetStepId) {
    return produce(state, (draft) => {
      if (draft.next === targetStepId) {
        draft.next = undefined;
      }
    });
  },
  layout() {
    return BlockLayout.FULL;
  },
  toModel(state, id) {
    return {
      customTitle: state.customTitle,
      type: ModelStep.OVERRIDE_ADJUSTMENT,
      next: state.next ? id(state.next) : undefined,
      adjustment: {
        flat: +state.adjustmentFlat,
        proportion: +state.adjustmentPercent / 100 + 1,
        currencyOverrides: state.currencyOverrides
          ? Object.fromEntries(
              Object.entries(state.currencyOverrides)
                .filter(([, v]) => v !== undefined && !isNaN(+v))
                .map(([k, v]) => [k, +v]),
            )
          : undefined,
        perCurrencyAmountsEnabled: state.perCurrencyAmountsEnabled,
      },
      isUseItOrLoseIt: state.isUseItOrLoseIt,
    };
  },
  fromModel(model, id) {
    return overrideAdjustmentFromModel(model, id);
  },
});

export { type OverrideAdjustmentState } from "@redotech/redo-model/return-flow-step-valid";
